/// <reference types="@angular/localize" />

import {platformBrowserDynamic} from '@angular/platform-browser-dynamic';
import {enableProdMode} from '@angular/core';
import {LicenseManager} from '@ag-grid-enterprise/core';
import * as Sentry from '@sentry/angular';
import {datadogLogs} from '@datadog/browser-logs';

import {AppModule} from './app/app.module';
import {environment} from '@environments/environment';
import {version as appVersion} from '@environments/version';
import {initOtel} from './telemetry/otel';
import {initDatadogRum} from './telemetry/datadog-rum';
import {stripLogsEventUrls} from './telemetry/telemetry.util';

// Initialize Sentry.
// The DSN is injected at runtime by the container's nginx sub_filter. A real DSN is
// always a URL, so a non-empty value that isn't one means the substitution didn't run
// (e.g. the JS was served pre-compressed via gzip_static, or SENTRY_DSN wasn't injected).
// An empty DSN is the intended "Sentry disabled" state.
const sentryDsn = environment.sentry?.dsn;
const sentryDsnIsValid = typeof sentryDsn === 'string' && sentryDsn.startsWith('http');

if (sentryDsn && !sentryDsnIsValid) {
  console.warn(
    '[Sentry] DSN was not replaced at runtime (got a non-URL value); Sentry is disabled. ' +
    'Check that the nginx sub_filter ran (not bypassed by gzip_static, SENTRY_DSN provided to the container).'
  );
} else if (environment.sentry?.enabled && sentryDsnIsValid) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    integrations: [
      // Network span instrumentation is owned by OpenTelemetry (instrumentation-fetch /
      // instrumentation-xml-http-request). Disable Sentry's fetch/XHR tracing to avoid
      // double-instrumenting requests and emitting redundant trace headers.
      Sentry.browserTracingIntegration({
        traceFetch: false,
        traceXHR: false,
      }),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: environment.sentry.tracesSampleRate,
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    release: appVersion,
    beforeSend(event) {
      // Filter out development errors if needed
      return event;
    },
  });
}

// OpenTelemetry init is best-effort: a failure here must never prevent Angular from bootstrapping.
try {
  initOtel();
} catch (err) {
  console.error('OpenTelemetry init failed', err);
}

const initDatadogBrowser = (clientToken: string | undefined): void => {
  if (!clientToken) {
    // The endpoint answered but carried no token — typically a container/env misconfiguration.
    // Warn rather than returning silently: without a token neither RUM nor browser logs start, so
    // this failure would otherwise be invisible from every telemetry pipeline at once.
    console.warn(
      '[Datadog] client token endpoint returned an empty token; RUM and browser logs are disabled.'
    );
    return;
  }
  datadogLogs.init({
    clientToken,
    site: environment.datadog.site,
    service: environment.datadog.service,
    env: environment.env,
    forwardConsoleLogs: environment.datadog.forwardConsoleLogs,
    forwardErrorsToLogs: environment.datadog.forwardErrorsToLogs,
    sessionSampleRate: environment.datadog.sessionSampleRate,
    beforeSend: (event) => {
      stripLogsEventUrls(event);
      return true;
    },
  });
  initDatadogRum(clientToken);
};

if (environment.datadog.enabled) {
  fetch('/v2/api/public-config/frontend-datadog-client-token')
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Datadog token fetch failed with status: ${res.status}`);
      }
      return res.json();
    })
    .then((jsonObj: { data: string; status: string }) => {
      initDatadogBrowser(jsonObj?.data);
    })
    .catch((err) => {
      console.error('Datadog browser-logs init failed', err);
      // Secondary signal only. Sentry and Datadog share the runtime-config failure mode that most
      // often causes this, so both can be dark at the same time; the authoritative alert is the
      // server-side "no RUM views received" monitor. No-ops when Sentry never initialized.
      Sentry.captureException(err);
    });
}

LicenseManager.setLicenseKey(environment.agGridLicense);

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic().bootstrapModule(AppModule)
  .catch(err => console.error(err));
